import {useTranslation} from 'react-i18next'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {ActionDetailSheet} from '@/components/action-detail-sheet'
import {ActionFormDialog} from '@/components/action-form-dialog'
import {AnalisisCausaDialog} from '@/components/analisis/analisis-causa-dialog'
import {useDeleteAction, type SgsstAction} from '@/hooks/sgsst/plan/use-actions'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import type {SgsstItem} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {translateApiError} from '@/lib/errors'

export type Analizando = {id: string; descripcion: string}

export function PlanDialogos({
  businessId,
  editable,
  eficaciaEditable,
  canOperate,
  formOpen,
  onFormOpenChange,
  formStandard,
  editing,
  suggestedTitulo,
  roster,
  evidence,
  notesAction,
  onNotesAction,
  analizando,
  onAnalizando,
  deleting,
  onDeleting,
  onDeleteError,
}: {
  businessId: string
  editable: boolean
  eficaciaEditable: boolean
  canOperate: boolean
  formOpen: boolean
  onFormOpenChange: (open: boolean) => void
  formStandard: SgsstItem | null
  editing: SgsstAction | null
  suggestedTitulo: string | null
  roster: Employee[]
  evidence: Evidence[]
  notesAction: SgsstAction | null
  onNotesAction: (action: SgsstAction | null) => void
  analizando: Analizando | null
  onAnalizando: (analizando: Analizando | null) => void
  deleting: SgsstAction | null
  onDeleting: (action: SgsstAction | null) => void
  onDeleteError: (message: string) => void
}) {
  const {t} = useTranslation()
  const deleteAction = useDeleteAction(businessId)
  return (
    <>
      <ActionFormDialog
        businessId={businessId}
        open={formOpen}
        onOpenChange={onFormOpenChange}
        standardCode={formStandard?.code ?? editing?.standard_code ?? null}
        standardName={formStandard?.name}
        action={editing}
        roster={roster}
        evidence={evidence}
        initialDescription={suggestedTitulo ?? undefined}
      />

      <ActionDetailSheet
        businessId={businessId}
        action={notesAction}
        editable={editable}
        eficaciaEditable={eficaciaEditable}
        canOperate={canOperate}
        onAnalizarCausa={(action) => {
          onNotesAction(null)
          onAnalizando({id: action.id, descripcion: action.description})
        }}
        open={!!notesAction}
        onOpenChange={(open) => !open && onNotesAction(null)}
      />

      <AnalisisCausaDialog
        businessId={businessId}
        actionId={analizando?.id ?? null}
        descripcion={analizando?.descripcion ?? ''}
        // Sigue a la acción, no al año: un «no eficaz» suele llegar con el año ya cerrado.
        editable={canOperate}
        open={!!analizando}
        onOpenChange={(open) => !open && onAnalizando(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && onDeleting(null)}
        title={t('plan.deleteTitle')}
        description={
          t('plan.deleteBody', {description: deleting?.description ?? ''}) +
          // El análisis de causa es hijo de la acción y se borra con ella.
          (deleting?.tiene_analisis ? ` ${t('plan.deleteConAnalisis')}` : '')
        }
        confirmLabel={t('plan.deleteConfirm')}
        cancelLabel={t('common.cancel')}
        pending={deleteAction.isPending}
        onConfirm={() =>
          deleteAction.mutate(deleting!.id, {
            onSuccess: () => onDeleting(null),
            onError: (err) => {
              onDeleting(null)
              onDeleteError(translateApiError(err))
            },
          })
        }
      />
    </>
  )
}
